<template>
    <div v-if="!hasMessages && isLoading" class="contact-view__spinner">
        <i class="fa fa-circle-o-notch fa-spin"></i>
    </div>

    <template v-if="!hasMessages">
        <div class="contact-view__no-data">Contact has no message history</div>
    </template>

    <template v-else v-for="(message, msgIndex) in messages" :key="message.id">
        <span v-if="shouldShowDateSlug(message, msgIndex)" class="c-chat__divider" :key="message.id + '_date_slug'" data-test="message-date-slug">
            {{ weekDayOfMonth(message.created_at) }}
        </span>

        <vx-message-cloud
            :messageId="message.id"
            :mediaUrl="message.media_url"
            :mediaContentType="message.media_content_type"
            :messageBody="message.body"
            :isInbound="message.direction === 'inbound'"
        >
            <template v-slot:info>
                <template v-if="message.direction === 'inbound'">
                    {{timeAmPm(message.created_at)}}
                </template>
                <template v-else-if="message.origin_meta?.type && message.origin_meta?.description">
                    {{message.origin_meta?.type}} - {{message.origin_meta?.description}} - {{timeAmPm(message.created_at)}}
                </template>
                <template v-else-if="message.origin_meta?.type">
                    {{message.origin_meta?.type}} - {{timeAmPm(message.created_at)}}
                </template>
                <template v-else>
                    {{timeAmPm(message.created_at)}}
                </template>
            </template>
        </vx-message-cloud>
    </template>

    <vx-button
        v-if="hasMessages && hasMoreMessages"
        class="contact-view__load-more"
        appearance="transparent-dark-outline"
        size="large"
        :disabled="isLoading"
        @click="loadMoreMessages"
    >
        Load More
    </vx-button>
</template>

<script setup>
import { ref, computed } from 'vue';
import { VxMessageCloud, VxButton } from '@voxie/frontend-components-v3';
import { useToasted } from '../../../composables/useToasted';
import { timeAmPm, weekDayOfMonth } from '../../filters';
import { messagesClient } from '../../../services';
import dayjs from '~/utils/dayjs';

const props = defineProps({
    contact: {
        type: Object,
        required: true,
    },
});

const $toasted = useToasted();

const messages = ref([]);
const nextPage = ref(null);
const isLoading = ref(false);

const hasMoreMessages = computed(() => nextPage.value);
const hasMessages = computed(() => messages.value.length > 0);

const loadMessages = (url) => {
    isLoading.value = true;

    const request = url ?
        messagesClient.listMessagesUsingLink(url) :
        messagesClient.listMessages(Spark.state.currentTeam.id, props.contact.id, {sort_dir: 'desc'})

    request.then(({ data }) => {
            messages.value = [...messages.value, ...data.data];
            nextPage.value = data.next_page_url;
        })
        .catch(() => {
            $toasted.global.platform_error();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const loadMoreMessages = () => {
    if (nextPage.value) {
        loadMessages(nextPage.value);
    }
};

const shouldShowDateSlug = (message, index) => {
    if (index == 0) return true;

    let prevMessage = messages.value[index - 1];

    return !!prevMessage && !dayjs(prevMessage.created_at).isSame(dayjs(message.created_at), 'day');
};

loadMessages();
</script>

<style lang="scss"></style>
